import React, { useContext, useEffect, useState } from 'react'
import { StateContext } from '../../services/StateService.jsx'
import { Translations } from '../../services/TranslationService.jsx'
import { getLoadMoreURL } from '../../services/URLService.jsx'
import { NO_IMAGE } from '../../constants/constants.js'
import { getRequest } from '../../services/AxiosService.jsx'
import ProductBar from '@3mcom/mds-library/dist/node/ProductBar'
import { useIsDesktop } from '../../services/UtilityService.jsx'
import { trackPDPData } from '../../services/TealiumService.jsx'
import Tabs from '@3mcom/mds-library/dist/node/Tabs'
import Alert from '@3mcom/mds-library/dist/node/Alert'
import '../../css/RelatedProducts.scss'

const RelatedProducts = ({ relatedProductsRef }) => {
    const [tabsData, setTabsData] = useState(null)
    const [activeTab, setActiveTab] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const isDesktop = useIsDesktop()

    const translations = useContext(Translations)

    const { relatedProductsTemp, showMore, ajaxError } = translations

    const {
        relatedProductsMore,
        dispatch,
        relatedProducts: relatedProductsList,
        endpoints,
        relatedProductsAggs,
        relatedProductsType,
        errors
    } = useContext(StateContext)

    useEffect(() => {
        if (relatedProductsList.length > 0) {
            // On the first render the first active tab will always be the relatedProductsAggs[0]
            // and the relatedProductsList on page load will be it's relatedProductsList
            const tabName = relatedProductsAggs[0]?.typeCode
            if (tabName) {
                setActiveTab(tabName)
                setTabsData({
                    [tabName]: {
                        relatedProducts: relatedProductsList,
                        relatedProductsMore: relatedProductsMore
                    }
                })
            }
        }
    }, [])

    useEffect(() => {
        if (activeTab !== '' && !tabsData[activeTab]) {
            // get the new resources based on the activeTab
            loadMoreRelatedProducts()
        }
        if (errors?.relatedProductsAjaxError) {
            dispatch({
                type: 'set_AJAX_errors',
                payload: { relatedProductsAjaxError: false }
            })
        }
    }, [activeTab])

    useEffect(() => {
        if (relatedProductsType !== '' && activeTab !== '') {
            // relatedProductsType will be created after we clicked on a new tab the asynchronus ones
            // the first one is the only tab loaded at the beginning so it will
            // append new resources if the relatedProductsType exist or it will create a new key with the relatedProductsType and the relatedProducts List
            // and relatedProductsMore of the specific tab
            setTabsData((prevState) => ({
                ...prevState,
                [relatedProductsType]: {
                    relatedProducts: [
                        ...(prevState[relatedProductsType]
                            ? prevState[relatedProductsType].relatedProducts
                            : []),
                        ...relatedProductsList
                    ],
                    relatedProductsMore: relatedProductsMore
                }
            }))
            setIsLoading(false)
        }
    }, [relatedProductsType, relatedProductsList])

    const loadMoreRelatedProducts = () => {
        const tabRelatedProductsLength = tabsData[activeTab]
            ? tabsData[activeTab].relatedProducts.length
            : 0

        let endpoint = getLoadMoreURL(
            `${endpoints.moreRelatedProducts}/`,
            tabRelatedProductsLength,
            4
        )

        endpoint = `${endpoint}&typeCode=${activeTab}`

        getRequest(endpoint)
            .then((response) => {
                if (response.data) {
                    dispatch({
                        type: 'pdp_load_more_related_products',
                        payload: {
                            ...response.data,
                            relatedProductsType: activeTab
                        }
                    })
                }
            })
            .catch((error) => {
                dispatch({
                    type: 'set_AJAX_errors',
                    payload: { relatedProductsAjaxError: true }
                })
                dispatch({
                    type: 'show_error_message',
                    payload: `Error on loading more RelatedProducts: ${error}`
                })
            })

        trackPDPData(`PDP Related Products Button Click: ${showMore}`, 'Engagement Event')
    }

    const onClickLoadMoreButton = () => {
        if (!isLoading) {
            setIsLoading(true)
            loadMoreRelatedProducts()
        }
    }

    return (
        <div
            ref={relatedProductsRef}
            className="sps2-pdp_section sps2-pdp_relatedProducts"
        >
            <h3 className="mds-font_header--3" tabIndex="-1">
                {relatedProductsTemp}
            </h3>
            {relatedProductsAggs?.length > 0 && tabsData && (
                <Tabs.TabsContainer defaultActive={0} alignment={'left'}>
                    {relatedProductsAggs?.map((tab, index) => (
                        <Tabs.TabItem
                            key={index}
                            title={`${tab.label} (${tab.count})`}
                            onClick={() => setActiveTab(tab.typeCode)}
                        >
                            <ProductBar.Container
                                loadMore={tabsData[activeTab]?.relatedProductsMore}
                            >
                                {activeTab !== '' &&
                                    tabsData[activeTab]?.relatedProducts.map(
                                        (product, index) => {
                                            return (
                                                <ProductBar.Item key={index}>
                                                    <a
                                                        className="mds-link"
                                                        href={product?.url}
                                                    >
                                                        <ProductBar.Image
                                                            src={
                                                                product?.image || NO_IMAGE
                                                            }
                                                            alt={product?.altText}
                                                            loading="lazy"
                                                        />
                                                    </a>
                                                    <h6 className="sps2-pdp_relatedProducts--category mds-font_header--6">
                                                        {product?.label}
                                                    </h6>
                                                    <ProductBar.Link
                                                        href={product?.url}
                                                        hrefClass="mds-link mds-link_product"
                                                        onClick={() =>
                                                            trackPDPData(
                                                                `PDP Related Products Link Click: ${product?.name}`,
                                                                'Engagement Event'
                                                            )
                                                        }
                                                    >
                                                        {product?.name}
                                                    </ProductBar.Link>
                                                </ProductBar.Item>
                                            )
                                        }
                                    )}
                                {!errors?.relatedProductsAjaxError &&
                                    ((isDesktop &&
                                        tabsData[activeTab]?.relatedProducts?.length %
                                            4 !==
                                            0) ||
                                        (!isDesktop &&
                                            tabsData[activeTab]?.relatedProducts?.length %
                                                2 !==
                                                0)) && (
                                        <ProductBar.Item
                                            className="mds-prodBar_item sps2-pdp_blank-item"
                                            tabIndex="-1"
                                        ></ProductBar.Item>
                                    )}
                                {tabsData[activeTab]?.relatedProductsMore && (
                                    <ProductBar.LoadMore>
                                        <button
                                            onClick={onClickLoadMoreButton}
                                            className="mds-button mds-button_tertiary mds-button_tertiary--large "
                                        >
                                            {showMore}
                                        </button>
                                    </ProductBar.LoadMore>
                                )}
                                {errors?.relatedProductsAjaxError && (
                                    <div className="sps2-pdp_ajaxError--centered mds-margin_medium--top">
                                        <Alert.InContext variant="error">
                                            {ajaxError}
                                        </Alert.InContext>
                                    </div>
                                )}
                            </ProductBar.Container>
                        </Tabs.TabItem>
                    ))}
                </Tabs.TabsContainer>
            )}
        </div>
    )
}

export default RelatedProducts
